import type { Area } from "react-easy-crop"
import { useCallback, useState } from "react"
import Cropper from "react-easy-crop"

import { Button, Dialog } from "@productlane/ui"

interface AvatarCropDialogProps {
	isOpen: boolean
	onOpenChange: (value: boolean) => void
	imageSrc: string
	fileName: string
	fileType: string
	title?: string
	description?: string
	onCropComplete: (file: File, previewUrl: string) => void
}

const OUTPUT_SIZE = 512

export function AvatarCropDialog({
	isOpen,
	onOpenChange,
	imageSrc,
	fileName,
	fileType,
	title = "Crop your avatar",
	description = "Drag to reposition and use the slider to zoom.",
	onCropComplete,
}: AvatarCropDialogProps) {
	const [crop, setCrop] = useState({ x: 0, y: 0 })
	const [zoom, setZoom] = useState(1)
	const [croppedAreaPixels, setCroppedAreaPixels] = useState<Area | null>(null)
	const [isProcessing, setIsProcessing] = useState(false)

	const onCropAreaChange = useCallback((_: Area, areaPixels: Area) => {
		setCroppedAreaPixels(areaPixels)
	}, [])

	const handleConfirm = async () => {
		if (!croppedAreaPixels) return
		setIsProcessing(true)
		try {
			const { file, previewUrl } = await getCroppedImage(
				imageSrc,
				croppedAreaPixels,
				fileName,
				fileType,
			)
			onCropComplete(file, previewUrl)
			onOpenChange(false)
		} finally {
			setIsProcessing(false)
		}
	}

	return (
		<Dialog
			isOpen={isOpen}
			onOpenChange={onOpenChange}
			title={title}
			description={description}
			footer={
				<div className="flex w-full items-center justify-end gap-2">
					<Button
						variant="outline"
						onClick={() => onOpenChange(false)}
						disabled={isProcessing}
					>
						Cancel
					</Button>
					<Button
						onClick={handleConfirm}
						disabled={!croppedAreaPixels || isProcessing}
					>
						{isProcessing ? "Saving…" : "Apply"}
					</Button>
				</div>
			}
		>
			<div className="flex flex-col gap-4">
				<div className="relative h-72 w-full overflow-hidden rounded-md bg-gray-100 dark:bg-gray-850">
					<Cropper
						image={imageSrc}
						crop={crop}
						zoom={zoom}
						aspect={1}
						cropShape="rect"
						showGrid={false}
						onCropChange={setCrop}
						onZoomChange={setZoom}
						onCropComplete={onCropAreaChange}
						classes={{ cropAreaClassName: "!rounded-md" }}
					/>
				</div>
				<div className="flex items-center gap-3">
					<span className="text-xs text-tertiary">Zoom</span>
					<input
						type="range"
						min={1}
						max={3}
						step={0.01}
						value={zoom}
						onChange={(e) => setZoom(Number(e.target.value))}
						className="flex-1 accent-gray-900 dark:accent-gray-100"
					/>
				</div>
			</div>
		</Dialog>
	)
}

async function getCroppedImage(
	imageSrc: string,
	area: Area,
	fileName: string,
	fileType: string,
): Promise<{ file: File; previewUrl: string }> {
	const image = await loadImage(imageSrc)
	const canvas = document.createElement("canvas")
	canvas.width = OUTPUT_SIZE
	canvas.height = OUTPUT_SIZE
	const ctx = canvas.getContext("2d")
	if (!ctx) throw new Error("Could not create canvas context")

	ctx.drawImage(
		image,
		area.x,
		area.y,
		area.width,
		area.height,
		0,
		0,
		OUTPUT_SIZE,
		OUTPUT_SIZE,
	)

	const outputType = fileType === "image/jpeg" ? "image/jpeg" : "image/png"
	const blob = await new Promise<Blob | null>((resolve) =>
		canvas.toBlob(resolve, outputType, 0.92),
	)
	if (!blob) throw new Error("Failed to create cropped image")

	const ext = outputType === "image/jpeg" ? "jpg" : "png"
	const baseName = fileName.replace(/\.[^.]+$/, "") || "avatar"
	const file = new File([blob], `${baseName}.${ext}`, { type: outputType })
	return { file, previewUrl: URL.createObjectURL(blob) }
}

function loadImage(src: string): Promise<HTMLImageElement> {
	return new Promise((resolve, reject) => {
		const image = new Image()
		image.crossOrigin = "anonymous"
		image.addEventListener("load", () => resolve(image))
		image.addEventListener("error", (err) => reject(err))
		image.src = src
	})
}
